import { useState, useEffect } from 'react';
import { nomeDoPapel } from '../utils/nomes';
import { AppLayout } from '../components/Layout/AppLayout';
import { useCredentials, Credential, BulkUpdateResult } from '../hooks/useCredentials';
import { useAuth } from '../hooks/useAuth';
import { CredentialCard } from '../components/Credentials/CredentialCard';
import { CredentialListRow } from '../components/Credentials/CredentialListRow';
import { CredentialForm } from '../components/Credentials/CredentialForm';
import { BulkUpdateModal } from '../components/Credentials/BulkUpdateModal';
import { ImportModal } from '../components/Credentials/ImportModal';
import { HolderManager } from '../components/Credentials/HolderManager';
import { MoveToHolderModal } from '../components/Credentials/MoveToHolderModal';
import { LoadingState, EmptyState } from '../components/common/FeedbackStates';
import { Plus, Search, LayoutGrid, List, AlertTriangle, X, Info, ArrowDownAZ, ArrowUpAZ, FileSpreadsheet, Folder, ChevronRight, UserCog, Pencil } from 'lucide-react';
import { workspacesApi, Workspace } from '../api/workspaces';
import { apiClient } from '../api/client';
import { useHolders } from '../hooks/useHolders';
import { SEM_TITULAR, agruparPorTitular, chaveDaPasta, credencialCombinaComBusca } from '../utils/holders';
import { PageHeader } from '../components/common/PageHeader';
import { useFeedback } from '../components/common/Feedback';

type ViewMode = 'grid' | 'list';
// Etapa 10a: "pastas" (por titular, padrão) ou "lista" (a tela de sempre, com todas as credenciais).
type Organizacao = 'pastas' | 'lista';

const VIEW_MODE_KEY = 'lictor_credentials_view_mode';
const ORGANIZACAO_KEY = 'lictor_credentials_organizacao';
const DISCLAIMER_KEY = 'lictor_autofill_disclaimer_dismissed';
const SORT_ALPHA_KEY = 'lictor_credentials_sort_alpha';

export function Credentials() {
  const { credentials, isLoading, fetchCredentials, createCredential, updateCredential, deleteCredential, bulkUpdateCredentials } = useCredentials();
  const { membership } = useAuth();
  const isAdmin = membership?.role === 'admin' || membership?.role === 'owner';
  const { holders, fetchHolders, createHolder, updateHolder, archiveHolder, moveCredentials } = useHolders();
  const { confirmar, avisar } = useFeedback();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCred, setEditingCred] = useState<Credential | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isHolderManagerOpen, setIsHolderManagerOpen] = useState(false);
  // D23: titular que o "Gerenciar titulares" já abre editando (lápis ao lado do nome da pasta).
  const [titularEmEdicao, setTitularEmEdicao] = useState<string | null>(null);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [organizacao, setOrganizacao] = useState<Organizacao>(() => {
    return localStorage.getItem(ORGANIZACAO_KEY) === 'lista' ? 'lista' : 'pastas';
  });
  // Pasta aberta na visão Pastas (id do titular ou SEM_TITULAR); null = vendo as pastas.
  const [pastaAberta, setPastaAberta] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    return (localStorage.getItem(VIEW_MODE_KEY) as ViewMode) || 'grid';
  });
  const [disclaimerDismissed, setDisclaimerDismissed] = useState(() => {
    return localStorage.getItem(DISCLAIMER_KEY) === 'true';
  });
  const [sortAlpha, setSortAlpha] = useState(() => {
    return localStorage.getItem(SORT_ALPHA_KEY) === 'true';
  });

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('all');

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  useEffect(() => {
    fetchHolders();
  }, [fetchHolders]);

  useEffect(() => {
    localStorage.setItem(ORGANIZACAO_KEY, organizacao);
  }, [organizacao]);

  useEffect(() => {
    workspacesApi.list()
      .then(data => setWorkspaces(data || []))
      .catch(console.error);

    apiClient.get<any[]>('/groups')
      .then(data => setGroups(data || []))
      .catch(console.error);
  }, []);

  const handleSwitchWorkspace = async (orgId: string) => {
    try {
      const data = await workspacesApi.switch(orgId);
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch workspace', err);
      avisar('Falha ao alternar organização.', 'erro');
    }
  };

  useEffect(() => {
    localStorage.setItem(VIEW_MODE_KEY, viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem(SORT_ALPHA_KEY, String(sortAlpha));
  }, [sortAlpha]);

  const handleDismissDisclaimer = () => {
    setDisclaimerDismissed(true);
    localStorage.setItem(DISCLAIMER_KEY, 'true');
  };

  const handleCreate = () => {
    setEditingCred(null);
    setIsFormOpen(true);
  };

  const handleEdit = (cred: Credential) => {
    setEditingCred(cred);
    setIsFormOpen(true);
  };

  // M3: a exclusão confirma citando o nome da credencial.
  const handleDelete = async (id: string) => {
    const credencial = credentials.find(c => c.id === id);
    const nome = credencial?.label || 'esta credencial';
    const ok = await confirmar({
      titulo: `Excluir "${nome}"?`,
      mensagem: 'Login, senha e código 2FA desta credencial são apagados e ninguém do escritório consegue mais usá-la. Não dá para desfazer.',
      confirmar: 'Excluir credencial',
      perigo: true,
    });
    if (!ok) return;
    try {
      await deleteCredential(id);
      await fetchHolders();
      avisar(`"${nome}" foi excluída.`, 'sucesso');
    } catch (err: any) {
      avisar(err?.message || 'Erro ao excluir a credencial.', 'erro');
    }
  };

  const handleSubmit = async (payload: any) => {
    if (editingCred) {
      await updateCredential(editingCred.id, payload);
    } else {
      await createCredential(payload);
    }
    if ('holder_id' in payload) await fetchHolders();
  };

  const handleImported = async () => {
    // A importação pode ter criado titulares novos.
    await Promise.all([fetchCredentials(), fetchHolders()]);
  };

  const handleMoveSubmit = async (holderId: string | null) => {
    const result = await moveCredentials(selectedIds, holderId);
    await fetchCredentials();
    setIsMoveModalOpen(false);
    handleCancelSelection();
    avisar(`${result.updated.length} credencial(is) movida(s). ${result.skipped.length} pulada(s) por falta de permissão ou por não existirem mais.`, result.skipped.length ? 'info' : 'sucesso');
  };

  const abrirTitulares = (editarId: string | null) => {
    setTitularEmEdicao(editarId);
    setIsHolderManagerOpen(true);
  };

  const handleArchiveHolder = async (id: string) => {
    await archiveHolder(id);
    if (pastaAberta === id) setPastaAberta(null);
  };

  const handleToggleSelectionMode = () => {
    setSelectionMode(v => !v);
    setSelectedIds([]);
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleCancelSelection = () => {
    setSelectionMode(false);
    setSelectedIds([]);
  };

  const handleBulkSubmit = async (payload: { login_plaintext?: string; password_plaintext?: string; totp_secret_plaintext?: string }) => {
    const result: BulkUpdateResult = await bulkUpdateCredentials({ credential_ids: selectedIds, ...payload });
    setIsBulkModalOpen(false);
    handleCancelSelection();
    avisar(`${result.updated.length} credencial(is) atualizada(s). ${result.skipped.length} pulada(s) por falta de permissão ou por não existirem mais.`, result.skipped.length ? 'info' : 'sucesso');
  };

  const groupFilteredCredentials = credentials.filter(cred => {
    if (selectedGroup !== 'all') {
      const group = groups.find(g => g.id === selectedGroup);
      if (group && !group.credential_ids.includes(cred.id)) {
        return false;
      }
    }
    return true;
  });

  // A busca sempre procura em todas as credenciais, também na visão Pastas.
  const filteredCredentials = groupFilteredCredentials.filter(cred => credencialCombinaComBusca(cred, search));

  const sortedCredentials = sortAlpha
    ? [...filteredCredentials].sort((a, b) => (a.label || '').localeCompare(b.label || '', 'pt-BR', { sensitivity: 'base' }))
    : filteredCredentials;

  const emPastas = organizacao === 'pastas';
  const buscando = search.trim() !== '';
  // Pastas com as credenciais do filtro de grupo; titular vazio só aparece para quem organiza (sem filtro de grupo).
  const pastas = agruparPorTitular(groupFilteredCredentials, holders, isAdmin && selectedGroup === 'all');
  const pastaAtual = pastas.find(p => p.chave === pastaAberta) || null;
  const mostrandoPastas = emPastas && !buscando && !pastaAtual;
  // Credenciais na tela agora: na visão Pastas, só as da pasta aberta (a busca mostra todas).
  const visibleCredentials = emPastas && !buscando && pastaAtual
    ? sortedCredentials.filter(c => chaveDaPasta(c) === pastaAtual.chave)
    : sortedCredentials;
  const folderLabelOf = (cred: Credential) => (emPastas && buscando ? cred.holder_name || 'Sem titular' : undefined);

  const allFilteredSelected = visibleCredentials.length > 0 && visibleCredentials.every(c => selectedIds.includes(c.id));

  const handleToggleSelectAll = () => {
    if (allFilteredSelected) {
      const filteredIds = new Set(visibleCredentials.map(c => c.id));
      setSelectedIds(prev => prev.filter(id => !filteredIds.has(id)));
    } else {
      const filteredIds = visibleCredentials.map(c => c.id);
      setSelectedIds(prev => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  const selectAllBtnStyle = (allSelected: boolean): React.CSSProperties => ({
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: 'var(--text-xs)',
    fontWeight: 'var(--font-medium)',
    color: allSelected ? 'var(--color-text-secondary)' : 'var(--color-primary-400)',
    padding: '2px 0',
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  });

  return (
    <AppLayout>
      {/* Disclaimer Banner */}
      {!disclaimerDismissed && (
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'var(--space-3)',
          padding: 'var(--space-4) var(--space-5)',
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08), rgba(245, 158, 11, 0.03))',
          border: '1px solid rgba(245, 158, 11, 0.25)',
          borderRadius: 'var(--radius-lg)',
          marginBottom: 'var(--space-6)',
          position: 'relative',
        }}>
          <AlertTriangle size={20} style={{ color: '#f59e0b', flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: '#f59e0b', marginBottom: 'var(--space-1)' }}>
              Recomendação importante
            </p>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
              <strong>Desative o preenchimento automático de senhas do seu navegador</strong> (Chrome, Edge, Firefox) para evitar conflitos com senhas desatualizadas, preenchimento incorreto e bloqueios de conta. O Lictor deve ser a <strong>única fonte de credenciais</strong> para os sistemas dos tribunais. Acesse as configurações do seu navegador e desative o recurso "Oferecer para salvar senhas".
            </p>
          </div>
          <button 
            onClick={handleDismissDisclaimer}
            style={{ 
              background: 'transparent', 
              border: 'none', 
              color: 'var(--color-text-secondary)', 
              cursor: 'pointer', 
              padding: '2px',
              flexShrink: 0,
              transition: 'color 0.2s ease',
            }}
            onMouseOver={(e) => { e.currentTarget.style.color = '#f59e0b'; }}
            onMouseOut={(e) => { e.currentTarget.style.color = 'var(--color-text-secondary)'; }}
            title="Dispensar aviso"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* D23: no celular, os botões quebram linha em vez de passar da largura da tela. */}
      <PageHeader
        title="Credenciais"
        description="Gerencie os acessos aos sistemas dos tribunais."
        actions={isAdmin ? (
          <>
            <button type="button" className="btn btn--outline btn--sm" onClick={handleToggleSelectionMode}>
              {selectionMode ? 'Cancelar seleção' : 'Selecionar'}
            </button>
            <button type="button" className="btn btn--outline btn--sm" onClick={() => abrirTitulares(null)} title="Criar, editar e arquivar titulares (pastas por advogado)">
              <UserCog size={16} /> Titulares
            </button>
            <button type="button" className="btn btn--outline btn--sm" onClick={() => setIsImportModalOpen(true)}>
              <FileSpreadsheet size={16} /> Importar planilha
            </button>
            <button type="button" className="btn btn--primary btn--sm" onClick={handleCreate}>
              <Plus size={16} /> Nova credencial
            </button>
          </>
        ) : undefined}
      />

      {selectionMode && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 'var(--space-4)', padding: 'var(--space-4)',
          background: 'rgba(26, 154, 102, 0.08)', border: '1px solid rgba(26, 154, 102, 0.25)',
          borderRadius: 'var(--radius-lg)', marginBottom: 'var(--space-6)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-4)' }}>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)', fontWeight: 'var(--font-medium)' }}>
              {selectedIds.length} credencial(is) selecionada(s)
            </span>
            {visibleCredentials.length > 0 && !mostrandoPastas && (
              <button onClick={handleToggleSelectAll} style={selectAllBtnStyle(allFilteredSelected)}>
                {allFilteredSelected ? (search ? 'Desmarcar visíveis' : 'Desmarcar todos') : (search ? 'Selecionar visíveis' : 'Selecionar todos')}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
            <button onClick={handleCancelSelection} style={{ background: 'transparent', border: '1px solid var(--color-border-default)', color: 'var(--color-text-secondary)', padding: 'var(--space-2) var(--space-4)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 'var(--font-medium)' }}>
              Cancelar
            </button>
            <button
              onClick={() => setIsMoveModalOpen(true)}
              disabled={selectedIds.length === 0}
              style={{ background: 'transparent', border: '1px solid var(--color-border-default)', color: 'var(--color-text-primary)', padding: 'var(--space-2) var(--space-4)', borderRadius: 'var(--radius-md)', cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', fontWeight: 'var(--font-medium)', opacity: selectedIds.length === 0 ? 0.6 : 1 }}
            >
              Mover para titular…
            </button>
            <button
              onClick={() => setIsBulkModalOpen(true)}
              disabled={selectedIds.length === 0}
              style={{ background: 'var(--color-primary-600)', color: 'var(--color-primary-50)', border: 'none', padding: 'var(--space-2) var(--space-4)', borderRadius: 'var(--radius-md)', cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', fontWeight: 'var(--font-medium)', opacity: selectedIds.length === 0 ? 0.6 : 1 }}
            >
              Atualizar valor
            </button>
          </div>
        </div>
      )}

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 'var(--space-4)',
        marginBottom: 'var(--space-6)',
        background: 'var(--color-bg-elevated)',
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-border-default)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap', flex: 1, width: '100%' }}>
          {/* Search */}
          {credentials.length > 0 && (
            <div style={{ position: 'relative', minWidth: '240px', flex: '1 1 240px' }}>
              <input
                type="text"
                placeholder="Buscar credencial..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: 'var(--space-2) var(--space-4) var(--space-2) var(--space-10)',
                  background: 'var(--color-bg-root)',
                  border: '1px solid var(--color-border-default)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-text-primary)',
                  fontSize: 'var(--text-sm)',
                  outline: 'none',
                  transition: 'all var(--duration-base) var(--ease-out)',
                  boxShadow: 'var(--shadow-sm)'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-primary-500)';
                  e.currentTarget.style.background = 'var(--color-bg-surface)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-glow)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-border-default)';
                  e.currentTarget.style.background = 'var(--color-bg-root)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                }}
              />
              <Search
                size={16}
                style={{
                  position: 'absolute',
                  left: 'var(--space-3)',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--color-text-secondary)',
                }}
              />
            </div>
          )}

          {/* Organization filter (Workspace switcher) */}
          {workspaces.length > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flex: '1 1 auto', minWidth: '200px' }}>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>Organização:</span>
              <select
                value={workspaces.find(ws => ws.is_active)?.id || ''}
                onChange={(e) => handleSwitchWorkspace(e.target.value)}
                style={{
                  width: '100%',
                  padding: 'var(--space-2) var(--space-3)',
                  background: 'var(--color-bg-root)',
                  border: '1px solid var(--color-border-default)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-text-primary)',
                  fontSize: 'var(--text-sm)',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                {workspaces.map(ws => (
                  <option key={ws.id} value={ws.id}>
                    {ws.name} ({nomeDoPapel(ws.role)})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Group filter (Equipe) */}
          {groups.length > 0 && credentials.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flex: '1 1 auto', minWidth: '200px' }}>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>Equipe/Grupo:</span>
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                style={{
                  width: '100%',
                  padding: 'var(--space-2) var(--space-3)',
                  background: 'var(--color-bg-root)',
                  border: '1px solid var(--color-border-default)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-text-primary)',
                  fontSize: 'var(--text-sm)',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                <option value="all">Todos os Grupos</option>
                {groups.map(g => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Sort + View Mode controls */}
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
          {/* Sort A-Z button */}
          {credentials.length > 0 && (
            <button
              onClick={() => setSortAlpha(v => !v)}
              title={sortAlpha ? 'Remover ordenação A-Z' : 'Ordenar A-Z'}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-md)',
                background: sortAlpha ? 'rgba(26, 154, 102, 0.12)' : 'var(--color-bg-root)',
                border: sortAlpha ? '1px solid rgba(26, 154, 102, 0.4)' : '1px solid var(--color-border-default)',
                color: sortAlpha ? 'var(--color-primary-400)' : 'var(--color-text-secondary)',
                cursor: 'pointer',
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-medium)',
                transition: 'all 0.2s ease',
              }}
              onMouseOver={(e) => {
                if (!sortAlpha) {
                  e.currentTarget.style.borderColor = 'rgba(26, 154, 102, 0.3)';
                  e.currentTarget.style.color = 'var(--color-primary-400)';
                }
              }}
              onMouseOut={(e) => {
                if (!sortAlpha) {
                  e.currentTarget.style.borderColor = 'var(--color-border-default)';
                  e.currentTarget.style.color = 'var(--color-text-secondary)';
                }
              }}
            >
              {sortAlpha ? <ArrowDownAZ size={15} /> : <ArrowUpAZ size={15} />}
              A-Z
            </button>
          )}

          {/* M1: dois seletores com rótulo: como agrupar (por titular ou todas) e como exibir (cartões ou lista) */}
          {credentials.length > 0 && (
            <div role="group" aria-label="Agrupar" className="seletor-com-rotulo">
              <span className="seletor-com-rotulo__rotulo">Agrupar</span>
              <div className="seletor-segmentado">
                {(['pastas', 'lista'] as Organizacao[]).map(opcao => (
                  <button
                    key={opcao}
                    type="button"
                    aria-pressed={organizacao === opcao}
                    onClick={() => { setOrganizacao(opcao); setPastaAberta(null); }}
                    title={opcao === 'pastas' ? 'Uma pasta por titular' : 'Todas as credenciais juntas'}
                  >
                    {opcao === 'pastas' ? <><Folder size={14} /> Por titular</> : <>Todas</>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {credentials.length > 0 && (
            <div role="group" aria-label="Exibir" className="seletor-com-rotulo">
              <span className="seletor-com-rotulo__rotulo">Exibir</span>
              <div className="seletor-segmentado">
                <button type="button" aria-pressed={viewMode === 'grid'} onClick={() => setViewMode('grid')} title="Cartões em grade">
                  <LayoutGrid size={14} /> Cartões
                </button>
                <button type="button" aria-pressed={viewMode === 'list'} onClick={() => setViewMode('list')} title="Linhas compactas">
                  <List size={14} /> Lista
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <LoadingState />
      ) : credentials.length === 0 ? (
        <EmptyState 
          title="Nenhuma credencial" 
          description="Crie uma credencial para cada sistema/URL que seu escritório acessa. Isso permite o preenchimento automático pela extensão e reúne todos os acessos num só lugar." 
          action={isAdmin ? (
            <button type="button" onClick={handleCreate} className="btn btn--primary">
              <Plus size={18} /> Adicionar agora
            </button>
          ) : undefined} 
        />
      ) : filteredCredentials.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 'var(--space-12)', color: 'var(--color-text-secondary)' }}>
          <Search size={48} style={{ margin: '0 auto var(--space-4) auto', opacity: 0.5 }} />
          <p style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-medium)' }}>Nenhuma credencial encontrada</p>
          <p style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--space-1)' }}>
            Não encontramos resultados para "{search}". Tente buscar por outro termo.
          </p>
        </div>
      ) : mostrandoPastas ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 'var(--space-4)' }}>
          {pastas.map(pasta => (
            <button
              key={pasta.chave}
              onClick={() => setPastaAberta(pasta.chave)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: 'var(--space-5)',
                background: 'var(--color-bg-elevated)',
                border: '1px solid var(--color-border-default)',
                borderRadius: 'var(--radius-lg)',
                cursor: 'pointer',
                textAlign: 'left',
                color: 'var(--color-text-primary)',
              }}
            >
              <Folder size={28} style={{ color: pasta.chave === SEM_TITULAR ? 'var(--color-text-secondary)' : 'var(--color-primary-400)', flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 'var(--font-semibold)', fontSize: 'var(--text-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pasta.nome}</div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                  {[pasta.oab, `${pasta.quantidade} credencia${pasta.quantidade === 1 ? 'l' : 'is'}`].filter(Boolean).join(' · ')}
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <>
          {emPastas && pastaAtual && !buscando && (
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-4)', fontSize: 'var(--text-sm)', minWidth: 0 }}>
              <button onClick={() => setPastaAberta(null)} style={{ background: 'transparent', border: 'none', color: 'var(--color-primary-400)', cursor: 'pointer', padding: 0, fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
                Credenciais
              </button>
              <ChevronRight size={14} style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }} />
              <span style={{ color: 'var(--color-text-primary)', fontWeight: 'var(--font-medium)', minWidth: 0, overflowWrap: 'anywhere' }}>{pastaAtual.nome}</span>
              {pastaAtual.oab && <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-xs)' }}>{pastaAtual.oab}</span>}
              {/* D23: editar o próprio titular (nome/OAB) direto da pasta aberta (owner/admin). */}
              {isAdmin && holders.some(h => h.id === pastaAtual.chave) && (
                <button
                  type="button"
                  className="botao-icone"
                  onClick={() => abrirTitulares(pastaAtual.chave)}
                  title="Editar titular"
                  aria-label="Editar titular"
                >
                  <Pencil size={14} />
                </button>
              )}
            </div>
          )}
          {visibleCredentials.length === 0 ? (
            <p style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>
              Nenhuma credencial nesta pasta. Use "Selecionar" → "Mover para titular…" para trazer credenciais para cá.
            </p>
          ) : viewMode === 'grid' ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))', gap: 'var(--space-6)' }}>
              {visibleCredentials.map(cred => (
                <CredentialCard
                  key={cred.id}
                  credential={cred}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  selectionMode={selectionMode}
                  selected={selectedIds.includes(cred.id)}
                  onToggleSelect={handleToggleSelect}
                  folderLabel={folderLabelOf(cred)}
                />
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {visibleCredentials.map(cred => (
                <CredentialListRow
                  key={cred.id}
                  credential={cred}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  selectionMode={selectionMode}
                  selected={selectedIds.includes(cred.id)}
                  onToggleSelect={handleToggleSelect}
                  folderLabel={folderLabelOf(cred)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Tip: 1 credential per system */}
      {credentials.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          marginTop: 'var(--space-8)',
          padding: 'var(--space-3) var(--space-4)',
          background: 'rgba(26, 154, 102, 0.04)',
          border: '1px solid rgba(26, 154, 102, 0.12)',
          borderRadius: 'var(--radius-md)',
        }}>
          <Info size={14} style={{ color: 'var(--color-primary-400)', flexShrink: 0 }} />
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
            <strong style={{ color: 'var(--color-primary-300)' }}>Dica:</strong> Para melhor funcionamento da extensão, crie uma credencial por URL (sistema). Isso permite que o popup de preenchimento automático identifique corretamente cada sistema.
          </span>
        </div>
      )}

      {isFormOpen && (
        <CredentialForm
          initialData={editingCred}
          onSubmit={handleSubmit}
          onClose={() => setIsFormOpen(false)}
          holders={holders}
          defaultHolderId={emPastas && pastaAtual && pastaAtual.chave !== SEM_TITULAR ? pastaAtual.chave : null}
          onManageHolders={isAdmin ? () => abrirTitulares(null) : undefined}
        />
      )}

      {/* Depois do formulário: aberto pelo atalho do campo "Titular", fica por cima dele. */}
      {isHolderManagerOpen && (
        <HolderManager
          holders={holders}
          editingHolderId={titularEmEdicao}
          onCreate={createHolder}
          onUpdate={async (id, payload) => { await updateHolder(id, payload); await fetchCredentials(); }}
          onArchive={handleArchiveHolder}
          onClose={() => { setIsHolderManagerOpen(false); setTitularEmEdicao(null); }}
        />
      )}

      {isMoveModalOpen && (
        <MoveToHolderModal
          count={selectedIds.length}
          holders={holders}
          onSubmit={handleMoveSubmit}
          onClose={() => setIsMoveModalOpen(false)}
        />
      )}

      {isBulkModalOpen && (
        <BulkUpdateModal
          count={selectedIds.length}
          onSubmit={handleBulkSubmit}
          onClose={() => setIsBulkModalOpen(false)}
        />
      )}

      {isImportModalOpen && (
        <ImportModal
          onImported={handleImported}
          onClose={() => setIsImportModalOpen(false)}
        />
      )}
    </AppLayout>
  );
}
